import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, isSameMonth, isSameDay, isBefore, startOfDay,
} from "date-fns";
import { ChevronLeft, ChevronRight, Clock, CalendarDays, ArrowLeft, ArrowRight, Globe, Video } from "lucide-react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { useLanguage, dfLocale } from "../i18n";

const WEEKDAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

export default function Prenota() {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const loc = dfLocale[lang];
  const [step, setStep] = useState(1);
  const [types, setTypes] = useState([]);
  const [type, setType] = useState(null);
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [daysMap, setDaysMap] = useState({});
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", topic: "", message: "", privacy: false });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/appointment-types").then((r) => setTypes(r.data)).catch(() => toast.error(t("Errore nel caricamento delle tipologie.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!type) return;
    const month = format(cursor, "yyyy-MM");
    api.get(`/availability/days`, { params: { month, type_id: type.id } })
      .then((r) => setDaysMap(r.data.days))
      .catch(() => {});
  }, [type, cursor]);

  useEffect(() => {
    if (!type || !date) return;
    setSlots(null);
    setSlot(null);
    api.get("/availability", { params: { date: format(date, "yyyy-MM-dd"), type_id: type.id } })
      .then((r) => setSlots(r.data.slots))
      .catch(() => toast.error(t("Errore nel caricamento degli orari.")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, date]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const confirm = async () => {
    if (!form.privacy) {
      toast.error(t("Devi accettare l'informativa privacy per confermare."));
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/bookings", {
        type_id: type.id,
        date: format(date, "yyyy-MM-dd"),
        time: slot,
        lang,
        ...form,
      });
      navigate(`/prenota/conferma/${r.data.id}`);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(t("Questo orario non è più disponibile. Scegline un altro."));
        const r = await api.get("/availability", { params: { date: format(date, "yyyy-MM-dd"), type_id: type.id } });
        setSlots(r.data.slots);
        setSlot(null);
        setStep(2);
      } else {
        toast.error(e.response?.data?.detail || t("Errore durante la prenotazione. Riprova."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const stepValid =
    (step === 1 && type) ||
    (step === 2 && date && slot) ||
    (step === 3 && form.first_name && form.last_name && form.email && form.topic);

  const stepLabels = [t("Tipologia"), t("Data e ora"), t("I tuoi dati"), t("Conferma")];

  return (
    <>
      <Seo
        title={`${t("Prenota una consulenza")} — Alsera Studio`}
        description={t("Scegli il giorno e l'orario più comodi per te.")}
      />
      <section className="pt-32 pb-24 sm:pt-40" data-testid="prenota-page">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("Prenotazione")}</p>
            <h1 className="mt-4 text-4xl font-extrabold leading-[1.1] tracking-tight text-antracite sm:text-5xl">
              {t("Il tuo progetto merita il giusto spazio.")}
            </h1>
            <div className="mt-4 space-y-2">
              <p className="flex items-center gap-2 text-sm font-medium text-antracite/60" data-testid="prenota-timezone">
                <Globe className="h-4 w-4 text-petrolio" />
                {t("Tutti gli orari sono nel fuso orario italiano (Europa/Roma).")}
              </p>
              <p className="flex items-center gap-2 text-sm font-medium text-antracite/60" data-testid="prenota-meet-note">
                <Video className="h-4 w-4 text-petrolio" />
                {t("La consulenza si svolge online su Google Meet: riceverai il link per collegarti via email.")}
              </p>
            </div>
          </Reveal>

          {/* Step indicator */}
          <div className="mt-10 flex items-center gap-2" data-testid="booking-steps">
            {stepLabels.map((label, i) => (
              <div key={label} className="flex flex-1 items-center gap-2">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors duration-300 ${step > i ? "bg-petrolio text-avorio" : "bg-acqua text-petrolio/60"}`}>
                  {i + 1}
                </span>
                <span className={`hidden text-xs font-semibold sm:block ${step > i ? "text-petrolio" : "text-antracite/45"}`}>{label}</span>
                {i < 3 && <span className={`h-px flex-1 ${step > i + 1 ? "bg-petrolio" : "bg-salvia/40"}`} />}
              </div>
            ))}
          </div>

          <div className="card-soft mt-8 p-6 sm:p-10">
            {/* STEP 1 — tipologia */}
            {step === 1 && (
              <div data-testid="booking-step-type">
                <h2 className="text-xl font-bold tracking-tight text-antracite sm:text-2xl">{t("Che tipo di appuntamento ti serve?")}</h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {types.map((ty) => (
                    <button
                      key={ty.id}
                      type="button"
                      data-testid={`booking-type-${ty.id}`}
                      onClick={() => setType(ty)}
                      className={`rounded-2xl border p-5 text-left transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 ${
                        type?.id === ty.id ? "border-petrolio bg-acqua/60 shadow-md" : "border-salvia/40 bg-white hover:border-petrolio/50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="font-bold text-antracite">{t(ty.name)}</h3>
                        <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-acqua px-2.5 py-1 text-xs font-bold text-petrolio">
                          <Clock className="h-3 w-3" /> {ty.duration_minutes} min
                        </span>
                      </div>
                      {ty.price_label && <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-petrolio">{t(ty.price_label)}</p>}
                      <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(ty.description)}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* STEP 2 — data e ora */}
            {step === 2 && (
              <div data-testid="booking-step-calendar">
                <h2 className="text-xl font-bold tracking-tight text-antracite sm:text-2xl">{t("Scegli giorno e orario")}</h2>
                <p className="mt-1 text-sm text-antracite/60">{t(type.name)} · {type.duration_minutes} {t("minuti")}</p>
                <div className="mt-6 grid gap-8 lg:grid-cols-2">
                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <button type="button" data-testid="calendar-prev-month" onClick={() => setCursor((c) => addMonths(c, -1))} disabled={isSameMonth(cursor, new Date())} className="rounded-full border border-salvia/50 p-2 text-petrolio transition-colors hover:bg-acqua disabled:opacity-30" aria-label={t("Mese precedente")}>
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      <p className="font-display text-sm font-bold capitalize text-antracite" data-testid="calendar-month-label">
                        {format(cursor, "MMMM yyyy", { locale: loc })}
                      </p>
                      <button type="button" data-testid="calendar-next-month" onClick={() => setCursor((c) => addMonths(c, 1))} className="rounded-full border border-salvia/50 p-2 text-petrolio transition-colors hover:bg-acqua" aria-label={t("Mese successivo")}>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="grid grid-cols-7 gap-1 text-center">
                      {WEEKDAYS_IT.map((d, idx) => (
                        <span key={d} className="py-1 text-[0.65rem] font-bold uppercase tracking-wider text-antracite/45">
                          {format(new Date(2024, 0, 1 + idx), "EEE", { locale: loc })}
                        </span>
                      ))}
                      {days.map((d) => {
                        const key = format(d, "yyyy-MM-dd");
                        const inMonth = isSameMonth(d, cursor);
                        const past = isBefore(d, startOfDay(new Date()));
                        const available = inMonth && !past && daysMap[key];
                        const selected = date && isSameDay(d, date);
                        return (
                          <button
                            key={key}
                            type="button"
                            data-testid={`booking-day-${key}`}
                            disabled={!available}
                            onClick={() => setDate(d)}
                            className={`aspect-square rounded-xl text-sm font-semibold transition-colors duration-200 ${
                              selected
                                ? "bg-petrolio text-avorio shadow-md"
                                : available
                                  ? "bg-acqua/70 text-petrolio hover:bg-salvia/60"
                                  : "text-antracite/25"
                            } ${!inMonth ? "opacity-30" : ""}`}
                          >
                            {format(d, "d")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="mb-4 flex items-center gap-2 text-sm font-bold capitalize text-antracite" data-testid="slots-label">
                      <CalendarDays className="h-4 w-4 text-petrolio" />
                      {date ? format(date, "EEEE d MMMM yyyy", { locale: loc }) : t("Seleziona un giorno")}
                    </p>
                    {date && slots === null && <p className="text-sm text-antracite/50">{t("Caricamento…")}</p>}
                    {slots && slots.filter((s) => s.available).length === 0 && (
                      <p className="rounded-2xl bg-acqua/50 p-4 text-sm text-antracite/70" data-testid="slots-empty">
                        {t("Nessun orario disponibile in questa data. Prova un altro giorno.")}
                      </p>
                    )}
                    <div className="grid grid-cols-3 gap-2.5" data-testid="slots-grid">
                      {slots?.map((s) => (
                        <button
                          key={s.time}
                          type="button"
                          data-testid={`booking-slot-${s.time.replace(":", "-")}`}
                          disabled={!s.available}
                          onClick={() => setSlot(s.time)}
                          className={`rounded-xl px-3 py-2.5 text-sm font-bold transition-[background-color,transform] duration-200 ${
                            slot === s.time
                              ? "bg-petrolio text-avorio shadow-md"
                              : s.available
                                ? "bg-acqua/70 text-petrolio hover:-translate-y-0.5 hover:bg-salvia/60"
                                : "cursor-not-allowed bg-antracite/5 text-antracite/25 line-through"
                          }`}
                        >
                          {s.time}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 — dati */}
            {step === 3 && (
              <div data-testid="booking-step-form">
                <h2 className="text-xl font-bold tracking-tight text-antracite sm:text-2xl">{t("I tuoi dati")}</h2>
                <div className="mt-6 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="bk-name" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Nome *")}</label>
                    <input id="bk-name" data-testid="booking-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className="input-field" placeholder="Mario" />
                  </div>
                  <div>
                    <label htmlFor="bk-last" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Cognome *")}</label>
                    <input id="bk-last" data-testid="booking-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="input-field" placeholder="Rossi" />
                  </div>
                  <div>
                    <label htmlFor="bk-email" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Email")} *</label>
                    <input id="bk-email" data-testid="booking-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-field" placeholder="mario@esempio.it" />
                  </div>
                  <div>
                    <label htmlFor="bk-phone" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Telefono")}</label>
                    <input id="bk-phone" data-testid="booking-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-field" placeholder={t("Facoltativo")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="bk-topic" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Argomento della consulenza *")}</label>
                    <input id="bk-topic" data-testid="booking-topic" value={form.topic} onChange={(e) => set("topic", e.target.value)} className="input-field" placeholder={t("Es. nuovo sito per la mia attività")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="bk-message" className="mb-1.5 block text-xs font-bold uppercase tracking-[0.12em] text-antracite/60">{t("Messaggio")}</label>
                    <textarea id="bk-message" data-testid="booking-message" rows={4} value={form.message} onChange={(e) => set("message", e.target.value)} className="input-field resize-none" placeholder={t("Facoltativo: aggiungi dettagli utili per preparare l'incontro…")} />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4 — riepilogo */}
            {step === 4 && (
              <div data-testid="booking-step-review">
                <h2 className="text-xl font-bold tracking-tight text-antracite sm:text-2xl">{t("Riepilogo e conferma")}</h2>
                <dl className="mt-6 space-y-3 rounded-2xl bg-acqua/50 p-6 text-sm">
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Appuntamento")}</dt><dd className="font-bold text-antracite" data-testid="review-type">{t(type.name)}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Data")}</dt><dd className="font-bold capitalize text-antracite" data-testid="review-date">{format(date, "EEEE d MMMM yyyy", { locale: loc })}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Orario")}</dt><dd className="font-bold text-antracite" data-testid="review-time">{slot} (Europe/Rome)</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Durata")}</dt><dd className="font-bold text-antracite">{type.duration_minutes} {t("minuti")}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Nome")}</dt><dd className="font-bold text-antracite">{form.first_name} {form.last_name}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Email")}</dt><dd className="font-bold text-antracite">{form.email}</dd></div>
                  {form.phone && <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Telefono")}</dt><dd className="font-bold text-antracite">{form.phone}</dd></div>}
                  <div className="flex justify-between gap-4"><dt className="font-semibold text-antracite/60">{t("Argomento")}</dt><dd className="font-bold text-antracite">{form.topic}</dd></div>
                </dl>
                <p className="mt-4 flex items-center gap-2 rounded-2xl bg-acqua/40 p-4 text-sm font-medium text-antracite/70" data-testid="review-meet-note">
                  <Video className="h-4 w-4 shrink-0 text-petrolio" />
                  {t("La consulenza si svolge online su Google Meet: riceverai il link per collegarti via email.")}
                </p>
                <label className="mt-5 flex items-start gap-3 text-sm text-antracite/70">
                  <input type="checkbox" data-testid="booking-privacy" checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-1 h-4 w-4 rounded border-salvia/60 accent-petrolio" />
                  <span>{t("Ho letto l'informativa privacy e acconsento al trattamento dei miei dati per la gestione dell'appuntamento. *")}</span>
                </label>
              </div>
            )}

            {/* Navigazione */}
            <div className="mt-8 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                className="btn-secondary !px-5 !py-2.5 disabled:opacity-0"
                data-testid="booking-back"
              >
                <ArrowLeft className="h-4 w-4" /> {t("Indietro")}
              </button>
              {step < 4 ? (
                <button
                  type="button"
                  onClick={() => stepValid && setStep((s) => s + 1)}
                  disabled={!stepValid}
                  className="btn-primary !px-5 !py-2.5 disabled:opacity-40"
                  data-testid="booking-next"
                >
                  {t("Continua")} <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={confirm}
                  disabled={submitting || !form.privacy}
                  className="btn-primary !px-5 !py-2.5 disabled:opacity-40"
                  data-testid="booking-confirm"
                >
                  {submitting ? t("Conferma in corso…") : t("Conferma la prenotazione")}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
